import { useEffect, useRef } from 'react';
import { useCleanedImageData } from '~/features/background/useCleanedImageData';
import { useActiveClip } from '~/features/clips/selectors';
import { useStore } from '~/store';
import { useFrames } from '../useFrames';
import { isPatchFrameIndexValid, runMatches } from './runMatches';

export {
  canTemplateFitInFrame,
  extractCellImageData,
  extractPatchImageData,
  isPatchFrameIndexValid,
  patchCenterOffsetInPatchFrame,
} from './runMatches';
export { matchResultsEqual } from './slice';

export function selectTemplateMatchSourceImage(
  step: 'matte' | 'sprite',
  liveCleaned: ImageData | null,
  committedCleaned: ImageData | null,
): ImageData | null {
  if (step === 'sprite' && committedCleaned) return committedCleaned;
  return liveCleaned;
}

export function useTemplateMatch(): void {
  const activeClip = useActiveClip();
  const activeClipId = activeClip?.id ?? null;
  const patch = activeClip?.patch ?? null;
  const alignmentMode = activeClip?.alignmentMode ?? 'none';
  const setResults = useStore((s) => s.setMatchResults);
  const step = useStore((s) => s.workflow.step);
  const committedCleaned = useStore((s) => s.workflow.committedCleaned);
  const liveCleaned = useCleanedImageData();
  const cleaned = selectTemplateMatchSourceImage(step, liveCleaned, committedCleaned);
  const frames = useFrames({ includeMatches: false });
  const lastClearedClipId = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (lastClearedClipId.current === activeClipId) return;
    lastClearedClipId.current = activeClipId;
    setResults(null);
  }, [activeClipId, setResults]);

  useEffect(() => {
    if (alignmentMode !== 'reference-point' || !patch || !cleaned) {
      setResults(null);
      return;
    }
    if (!isPatchFrameIndexValid(frames, patch)) {
      setResults([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const results = await runMatches(cleaned, frames, patch);
        if (cancelled) return;
        setResults(results);
      } catch (error) {
        console.error('[reference-point] template matching failed', error);
        if (cancelled) return;
        setResults([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patch, alignmentMode, cleaned, frames, setResults]);
}
